import { useState, type ReactNode } from 'react'
import { Box, Group, ActionIcon, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCopy, IconKey, IconX } from '@tabler/icons-react'
import { useSessionApiKey } from '@/hooks/useSessionApiKey'
import { copyToClipboard } from '@/lib/clipboard'
import { PasteKeyModal } from './PasteKeyModal'

const PLACEHOLDER = '<YOUR_API_KEY>'

interface Props {
  code: string
  lang: string
  children: ReactNode
}

export function CodeBlockToolbar({ code, lang, children }: Props) {
  const { key, setKey, clearKey, hasKey } = useSessionApiKey()
  const [pasteOpen, setPasteOpen] = useState(false)
  const hasPlaceholder = code.includes(PLACEHOLDER)

  async function writeClip(text: string, title: string) {
    try {
      await copyToClipboard(text)
      notifications.show({ color: 'teal', title: 'Copiado', message: title })
    } catch {
      notifications.show({ color: 'red', title: 'Falha', message: 'Clipboard indisponível.' })
    }
  }

  function copyPlain() {
    void writeClip(code, 'Código copiado.')
  }

  function copyWithKey() {
    if (!hasKey) {
      setPasteOpen(true)
      return
    }
    const substituted = code.split(PLACEHOLDER).join(key!)
    void writeClip(substituted, 'Código copiado com sua key de sessão.')
  }

  function handlePasteSubmit(value: string) {
    setKey(value)
    setPasteOpen(false)
    const substituted = code.split(PLACEHOLDER).join(value)
    void writeClip(substituted, 'Key salva na sessão e código copiado.')
  }

  return (
    <Box pos="relative" data-lang={lang}>
      <Group gap="xs" justify="flex-end" pos="absolute" top={8} right={8} style={{ zIndex: 1 }}>
        <Tooltip label="Copiar código">
          <ActionIcon variant="subtle" onClick={copyPlain} aria-label="Copiar">
            <IconCopy size={14} />
          </ActionIcon>
        </Tooltip>
        {hasPlaceholder && (
          <>
            <Tooltip label={hasKey ? 'Copiar com minha key da sessão' : 'Colar key e copiar'}>
              <ActionIcon
                variant="subtle"
                onClick={copyWithKey}
                aria-label="Copiar com minha key"
              >
                <IconKey size={14} />
              </ActionIcon>
            </Tooltip>
            {hasKey && (
              <Tooltip label="Limpar key da sessão">
                <ActionIcon variant="subtle" onClick={clearKey} aria-label="Limpar key">
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </>
        )}
      </Group>
      {children}
      <PasteKeyModal
        opened={pasteOpen}
        onClose={() => setPasteOpen(false)}
        onSubmit={handlePasteSubmit}
      />
    </Box>
  )
}
